import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Post, Profile } from '@/lib/supabase/client';

// Extended Post type including profile data
export type PostWithProfile = Post & {
  profiles?: Pick<Profile, 'username' | 'display_name' | 'avatar_url'>;
};

interface PostState {
  // State
  posts: PostWithProfile[];
  draftPosts: PostWithProfile[];
  scheduledPosts: PostWithProfile[];
  currentPost: PostWithProfile | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchPosts: (page?: number, limit?: number) => Promise<void>;
  fetchDrafts: (page?: number, limit?: number) => Promise<void>;
  fetchScheduled: (page?: number, limit?: number) => Promise<void>;
  fetchPostById: (id: number) => Promise<void>;
  createPost: (post: Partial<Post>) => Promise<PostWithProfile | null>;
  updatePost: (id: number, updates: Partial<Post>) => Promise<void>;
  deletePost: (id: number) => Promise<void>;
  publishDraft: (id: number) => Promise<void>;
  schedulePost: (id: number, scheduledFor: string) => Promise<void>;
  clearError: () => void;
}

export const usePostStore = create<PostState>((set, get) => ({
  posts: [],
  draftPosts: [],
  scheduledPosts: [],
  currentPost: null,
  isLoading: false,
  error: null,
  
  fetchPosts: async (page = 1, limit = 10) => {
    set({ isLoading: true, error: null });
    
    // Calculate pagination offset
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*, profiles:author_id(username, display_name, avatar_url)')
        .eq('is_published', true)
        .is('scheduled_for', null) // Only get published posts that are not scheduled
        .order('created_at', { ascending: false })
        .range(from, to);
        
      if (error) throw error;
      
      set({ posts: data as PostWithProfile[], isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  // Fetch draft posts (unpublished posts)
  fetchDrafts: async (page = 1, limit = 10) => {
    set({ isLoading: true, error: null });
    
    // Calculate pagination offset
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      if (!user) {
        set({ error: 'You must be logged in to view drafts', isLoading: false });
        return;
      }
      
      const { data, error } = await supabase
        .from('posts')
        .select('*, profiles:author_id(username, display_name, avatar_url)')
        .eq('author_id', user.id)
        .eq('is_published', false)
        .is('scheduled_for', null) // Only get drafts that are not scheduled
        .order('updated_at', { ascending: false })
        .range(from, to);
        
      if (error) throw error;
      
      set({ draftPosts: data as PostWithProfile[], isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  // Fetch scheduled posts
  fetchScheduled: async (page = 1, limit = 10) => {
    set({ isLoading: true, error: null });
    
    // Calculate pagination offset
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      if (!user) {
        set({ error: 'You must be logged in to view scheduled posts', isLoading: false });
        return;
      }
      
      const { data, error } = await supabase
        .from('posts')
        .select('*, profiles:author_id(username, display_name, avatar_url)')
        .eq('author_id', user.id)
        .eq('is_published', false)
        .not('scheduled_for', 'is', null) // Only get posts with scheduled_for not null
        .order('scheduled_for', { ascending: true })
        .range(from, to);
        
      if (error) throw error;
      
      set({ scheduledPosts: data as PostWithProfile[], isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  fetchPostById: async (id: number) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*, profiles:author_id(username, display_name, avatar_url)')
        .eq('id', id)
        .single();
        
      if (error) throw error;
      
      set({ currentPost: data as PostWithProfile, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  createPost: async (post: Partial<Post>) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      if (!user) {
        set({ error: 'You must be logged in to create a post', isLoading: false });
        return null;
      }
      
      const newPost = {
        ...post,
        author_id: user.id,
        is_published: post.is_published !== undefined ? post.is_published : true,
        scheduled_for: post.scheduled_for || null
      };
      
      console.log('Creating post with data:', newPost);
      
      const { data, error } = await supabase
        .from('posts')
        .insert([newPost])
        .select()
        .single();
        
      if (error) {
        console.error('Error creating post:', error);
        throw error;
      }
      
      const createdPost = data as PostWithProfile;
      
      // Update the appropriate posts list based on post status
      set(state => {
        // Determine which array to update
        if (createdPost.is_published && !createdPost.scheduled_for) {
          return { 
            posts: [createdPost, ...state.posts],
            currentPost: createdPost,
            isLoading: false 
          };
        } else if (!createdPost.is_published && !createdPost.scheduled_for) {
          return { 
            draftPosts: [createdPost, ...state.draftPosts],
            currentPost: createdPost,
            isLoading: false 
          };
        } else if (!createdPost.is_published && createdPost.scheduled_for) {
          return { 
            scheduledPosts: [createdPost, ...state.scheduledPosts],
            currentPost: createdPost,
            isLoading: false 
          };
        }
        return { currentPost: createdPost, isLoading: false };
      });
      
      return data as PostWithProfile;
    } catch (error: any) {
      console.error('Post creation failed:', error);
      set({ error: error.message || 'Failed to create post', isLoading: false });
      return null;
    }
  },
  
  updatePost: async (id: number, updates: Partial<Post>) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('posts')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
        
      if (error) throw error;
      
      const updatedPost = data as PostWithProfile;
      
      // Update the appropriate arrays based on the post's new status
      set(state => {
        const newState: Partial<PostState> = {
          isLoading: false
        };
        
        // Update currentPost if it's the one being edited
        if (state.currentPost?.id === id) {
          newState.currentPost = updatedPost;
        }
        
        // Remove from all arrays first (we'll add back to the correct one)
        newState.posts = state.posts.filter(post => post.id !== id);
        newState.draftPosts = state.draftPosts.filter(post => post.id !== id);
        newState.scheduledPosts = state.scheduledPosts.filter(post => post.id !== id);
        
        // Add to the appropriate array based on post status
        if (updatedPost.is_published && !updatedPost.scheduled_for) {
          newState.posts = [updatedPost, ...newState.posts];
        } else if (!updatedPost.is_published && !updatedPost.scheduled_for) {
          newState.draftPosts = [updatedPost, ...newState.draftPosts];
        } else if (!updatedPost.is_published && updatedPost.scheduled_for) {
          newState.scheduledPosts = [updatedPost, ...newState.scheduledPosts];
        }
        
        return newState;
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  deletePost: async (id: number) => {
    set({ isLoading: true, error: null });
    
    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      // Remove the post from all posts lists
      set(state => ({ 
        posts: state.posts.filter(post => post.id !== id),
        draftPosts: state.draftPosts.filter(post => post.id !== id),
        scheduledPosts: state.scheduledPosts.filter(post => post.id !== id),
        currentPost: state.currentPost?.id === id ? null : state.currentPost,
        isLoading: false
      }));
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  // Publish a draft post immediately
  publishDraft: async (id: number) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('posts')
        .update({
          is_published: true,
          scheduled_for: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
        
      if (error) throw error;
      
      const publishedPost = data as PostWithProfile;
      
      // Update post lists
      set(state => {
        // Remove from drafts and scheduled
        const draftPosts = state.draftPosts.filter(post => post.id !== id);
        const scheduledPosts = state.scheduledPosts.filter(post => post.id !== id);
        
        // Add to published posts
        const posts = [publishedPost, ...state.posts.filter(post => post.id !== id)];
        
        return {
          posts,
          draftPosts,
          scheduledPosts,
          currentPost: state.currentPost?.id === id ? publishedPost : state.currentPost,
          isLoading: false
        };
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  // Schedule a post for future publishing
  schedulePost: async (id: number, scheduledFor: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const { data, error } = await supabase
        .from('posts')
        .update({
          is_published: false,
          scheduled_for: scheduledFor,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
        
      if (error) throw error;
      
      const scheduledPost = data as PostWithProfile;
      
      // Update post lists
      set(state => {
        // Remove from published and drafts
        const posts = state.posts.filter(post => post.id !== id);
        const draftPosts = state.draftPosts.filter(post => post.id !== id);
        
        // Add to scheduled posts
        const scheduledPosts = [scheduledPost, ...state.scheduledPosts.filter(post => post.id !== id)];
        
        return {
          posts,
          draftPosts,
          scheduledPosts,
          currentPost: state.currentPost?.id === id ? scheduledPost : state.currentPost,
          isLoading: false
        };
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },
  
  clearError: () => set({ error: null }),
})); 